import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type RefObject,
  type TouchEvent as ReactTouchEvent,
} from "react";
import {
  safeSessionStorageGetItem,
  safeSessionStorageSetItem,
} from "../../../../utils/localStorage";

interface UseResizableSplitPaneOptions {
  sessionKey: string;
  defaultRatio?: number;
  minLeftPaneWidth?: number;
  minRightPaneWidth?: number;
}

interface UseResizableSplitPaneResult {
  splitPaneRef: RefObject<HTMLDivElement>;
  leftPaneRatio: number;
  minLeftPaneWidth: number;
  minRightPaneWidth: number;
  handleResizeStart: (event: ReactMouseEvent<HTMLDivElement>) => void;
  handleResizeTouchStart: (event: ReactTouchEvent<HTMLDivElement>) => void;
}

export function useResizableSplitPane({
  sessionKey,
  defaultRatio = 0.68,
  minLeftPaneWidth = 560,
  minRightPaneWidth = 360,
}: UseResizableSplitPaneOptions): UseResizableSplitPaneResult {
  const splitPaneRef = useRef<HTMLDivElement>(null);
  const isResizingRef = useRef(false);

  const [leftPaneRatio, setLeftPaneRatio] = useState<number>(() => {
    if (typeof window === "undefined") {
      return defaultRatio;
    }

    const storedRatio = Number(safeSessionStorageGetItem(sessionKey));
    if (!Number.isNaN(storedRatio) && storedRatio > 0 && storedRatio < 1) {
      return storedRatio;
    }

    return defaultRatio;
  });

  const clampPaneRatio = useCallback(
    (ratio: number) => {
      const paneWidth = splitPaneRef.current?.clientWidth;
      if (!paneWidth) {
        return ratio;
      }

      const minRatio = minLeftPaneWidth / paneWidth;
      const maxRatio = 1 - minRightPaneWidth / paneWidth;

      if (minRatio > maxRatio) {
        return 0.5;
      }

      return Math.min(Math.max(ratio, minRatio), maxRatio);
    },
    [minLeftPaneWidth, minRightPaneWidth],
  );

  const resizeFromClientX = useCallback(
    (clientX: number) => {
      const paneElement = splitPaneRef.current;
      if (!paneElement) {
        return;
      }

      const rect = paneElement.getBoundingClientRect();
      const nextRatio = (clientX - rect.left) / rect.width;
      setLeftPaneRatio(clampPaneRatio(nextRatio));
    },
    [clampPaneRatio],
  );

  const handleResizeStart = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      isResizingRef.current = true;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [],
  );

  const handleResizeTouchStart = useCallback(
    (event: ReactTouchEvent<HTMLDivElement>) => {
      event.preventDefault();
      isResizingRef.current = true;
      document.body.style.userSelect = "none";

      const touchPoint = event.touches[0];
      if (touchPoint) {
        resizeFromClientX(touchPoint.clientX);
      }
    },
    [resizeFromClientX],
  );

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isResizingRef.current) {
        return;
      }
      resizeFromClientX(event.clientX);
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (!isResizingRef.current) {
        return;
      }

      const touchPoint = event.touches[0];
      if (!touchPoint) {
        return;
      }

      resizeFromClientX(touchPoint.clientX);
      event.preventDefault();
    };

    const stopResizing = () => {
      if (!isResizingRef.current) {
        return;
      }
      isResizingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopResizing);
    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", stopResizing);
    window.addEventListener("touchcancel", stopResizing);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopResizing);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", stopResizing);
      window.removeEventListener("touchcancel", stopResizing);
    };
  }, [resizeFromClientX]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    safeSessionStorageSetItem(sessionKey, String(leftPaneRatio));
  }, [leftPaneRatio, sessionKey]);

  useEffect(() => {
    const handleResize = () => {
      setLeftPaneRatio((prev) => clampPaneRatio(prev));
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [clampPaneRatio]);

  return {
    splitPaneRef,
    leftPaneRatio,
    minLeftPaneWidth,
    minRightPaneWidth,
    handleResizeStart,
    handleResizeTouchStart,
  };
}
